import React, { CSSProperties, ForwardedRef, forwardRef, ReactElement, useEffect, useImperativeHandle, useRef } from "react";
import { ReactNode, useMemo, useState } from "react";
import styled from 'styled-components'
import { randomNormal } from "../util/maths";
import { PROJECT_THEME } from "../theme";
import curry from "lodash/curry";
import { RedactedText } from "./Code";

const TerminalWrapper = styled.div`
    overflow: scroll;
    -ms-overflow-style: none;
    scrollbar-width: none;
    font-size: clamp(9px, 0.9vw, 17px);
    box-sizing: border-box;

    &::-webkit-scrollbar {
        display: none;
    }
`

interface TerminalProps{
    averageMessageSpeed: number
    messageSpeedStandardDeviance: number
    startMessage: number
    children: ReactNode
    style?: CSSProperties
}

function currentDateTime() {
    const now = new Date();

    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0'); // Months are zero-indexed
    const day = String(now.getDate()).padStart(2, '0');

    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    const seconds = String(now.getSeconds()).padStart(2, '0');

    const milliseconds = String(now.getMilliseconds()).padStart(3, '0');

    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds},${milliseconds}`;
}

const Buttons = styled.div`
    display: flex;  
    gap: 5px;
    margin-bottom: 1em;
`

export const Terminal = forwardRef<
    number, TerminalProps
>(({
    averageMessageSpeed,
    messageSpeedStandardDeviance,
    startMessage,
    children,
    style,
}, ref) => {

    const totalMessages = useMemo(() => React.Children.count(children), [children])
    const [messages, setMessages] = useState<number>(Math.min(totalMessages, startMessage))
    const wrapperRef = useRef<HTMLDivElement>(null)

    useImperativeHandle(ref, () => messages)

    useEffect(() => {

        if(wrapperRef.current)
            wrapperRef.current.scrollTop = wrapperRef.current.scrollHeight

        const waitTime = Math.max(50, randomNormal(averageMessageSpeed, messageSpeedStandardDeviance))
        if(messages === totalMessages) 
            return
        setTimeout(() => setMessages(messages + 1), waitTime)
        
    }, [averageMessageSpeed, messageSpeedStandardDeviance, messages, totalMessages])

    return (
        <TerminalWrapper style={style} ref={wrapperRef}>
            <Buttons>
                <svg width="10" height="10" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="5" cy="5" r="4" fill={PROJECT_THEME.colour.COLOUR_MID_LIGHT} />
                </svg>
                <svg width="10" height="10" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="5" cy="5" r="4" fill={PROJECT_THEME.colour.COLOUR_LIGHT} />
                </svg>
                <svg width="10" height="10" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="5" cy="5" r="4" fill={PROJECT_THEME.colour.COLOUR_DARK} />
                </svg>
            </Buttons>
            {
                React.Children.toArray(children).slice(0, messages)
            }
        </TerminalWrapper>
    )
})

interface TerminalMessageProps{
    children: ReactNode
    logLevel?: 'info' | 'warn' | 'error'
}

const Message = styled.p<{ level: 'info' | 'warn' | 'error'}>`
    font-family: ${({ theme }) => theme.font.FONT_MONO_JETBRAINS};
    font-size: 1em;
    color: ${({ level, theme }) => 
        level === 'info' ? theme.colour.COLOUR_MID :
        level === 'warn' ? theme.colour.COLOUR_LIGHT :
        level === 'error' ? theme.colour.COLOUR_MID_LIGHT : theme.colour.COLOUR_MID
    };
`

export const TerminalMessage = ({
    children, logLevel
}: TerminalMessageProps) => 
    <Message level={logLevel ?? 'info'}>
       {children}
    </Message>

interface TerminalCommandProps{
    children: string
}

const Command = styled.p`
    font-family: ${({ theme }) => theme.font.FONT_MONO_JETBRAINS};
    font-size: 1em;
    color: ${({ theme }) => theme.colour.COLOUR_MID_DARK};
    position: relative;
    right: 2px;
`

const CommandWrapper = styled.div`
    display: flex;
`

const User = curry(RedactedText)(PROJECT_THEME.colour.COLOUR_DARK)
const Hostname = curry(RedactedText)(PROJECT_THEME.colour.COLOUR_LIGHT)

export const TerminalCommand = ({
    children
}: TerminalCommandProps) => 
    <CommandWrapper>
        <User style={{marginRight: '0.3em'}}>
            Jake
        </User>
        <Command>
            @
        </Command>
        <Hostname style={{marginRight: '0.3em'}}>
            somehost
        </Hostname>
        <Command>
            $ {children}
        </Command>
    </CommandWrapper>
